import React, { Component } from 'react';

import BrandingService from '../fetch-branding.js';
import { getProfile, getCompanies, getSolutions, getMarketPlace, getRewards } from '../fetch-user-info.js';
import {
    FetchGlobalNavigation,
    FetchUserNavigation,
    FetchGlobalStandaloneNavigation,
    FetchUserStandaloneNavigation
} from '../fetch-navigation.js';
import FetchStrings from '../fetch-strings.js';
import { FetchNotifications } from '../fetch-notifications.js';
import { languageChangeEvent, setLanguageBundle } from '@nackle/intl-tools';
import { isObjEqual } from '../utils/utils.js';
import createStyles from '../utils/create-styles.js';

import Main from '../main-wrapper';

import './app-root.less';

export const DeckContext = React.createContext();
const { Provider, Consumer } = DeckContext;

export default class AppRoot extends Component {
    constructor( props ) {
        super( props );
        this.state = {
            contentLoaded: false,
            hasFetchedFromPiers: false,
            navigationData: {},
            notifications: [],
            hasFetchedFromNavigation: false,
            hasFetchFromNotifications: false,
            sidebarOpen: false
        };
        this.sidebarRef = React.createRef();
    }

    componentDidMount() {
        const { guest, locale, languageBundle, user } = this.props;

        languageChangeEvent.on( ( { localeCode, bundle } ) => {
            this.setState( {
                locale: localeCode
            } );
        } );

        if ( guest && locale && languageBundle ) {
            setLanguageBundle( locale, languageBundle );
        }

        // If the application already has a user with an access token when it initially
        // mounts Deck make the Piers call right away.
        if ( !guest && user?.access_token ) {
            this.setState( { hasFetchedFromPiers: true } );
            this.fetchPiersData();
            this.fetchNotifications();
            this.fetchBranding();
        }

    }

    componentDidUpdate( prevProps, prevState ) {
        const { locale, user, guest } = this.props;
        const { hasFetchedFromPiers, hasFetchedFromNavigation, hasFetchFromNotifications } = this.state;
        const prevBundle = prevProps.languageBundle || {};
        const bundle = this.props.languageBundle || {};

        if ( !guest ) {
            if ( !hasFetchedFromPiers && user?.access_token ) {
                this.setState( { hasFetchedFromPiers: true } );
                this.fetchPiersData();
                this.fetchBranding();
            }

            if ( !hasFetchedFromNavigation && user?.access_token ) {
                this.setState( { hasFetchedFromNavigation: true } );
                this.fetchNavigation();
            }

            if ( !hasFetchFromNotifications && user?.access_token ) {
                this.setState( { hasFetchFromNotifications: true } );
                this.fetchNotifications();
            }

            // If a consuming app has changed the locale prop, refetch the bundle
            if ( hasFetchedFromPiers && locale ) {
                if ( prevProps.locale !== locale ) {
                    this.fetchStrings();
                }
            }
        } else if ( guest ) {
            if ( !isObjEqual( prevBundle, bundle ) ) {
                setLanguageBundle( locale || 'en-US', bundle );
            }
        }
    }

    fetchBranding = async () => {
        if ( this.props.user ) {
            const brandingService = new BrandingService( this.props.user );
            const resp = await brandingService.getStyles( { elementName: [ 'color', 'logo' ] } );
            const formattedStyles = {};

            resp.styles.forEach( s => {
                if ( s.elementName === 'logo' && s.key === 'logo-url-primary' ) {
                    formattedStyles.primaryLogo = s.value;
                } else if ( s.elementName === 'color' ) {
                    if ( s.propertyName === 'primary' ) {
                        formattedStyles.primaryColor = s.value;
                    } else if ( s.propertyName === 'secondary' ) {
                        formattedStyles.secondaryColor = s.value;
                    }
                }
            } );

            this.setState( {
                branding: {
                    styles: resp.styles,
                    formattedStyles
                }
            } );

            createStyles( formattedStyles, 'head' );
        }
    };

    makePiersRequest = async( personId, accessToken, piersFunc ) => {
        try {
            return piersFunc( personId, accessToken );
        } catch ( error ) {
            console.error( 'An error occurred:', error.message );
            return null;
        }
    };

    fetchPiersData = async () => {
        let profileData, companiesData, solutionData, marketPlaceData, rewardsData;
        const {
            access_token,
            claims: { company_id, person_id }
        } = this.props.user;
        if ( person_id && access_token ) {
            profileData = await this.makePiersRequest( person_id, access_token, getProfile );
            companiesData = await this.makePiersRequest( person_id, access_token, getCompanies );
            solutionData = await this.makePiersRequest( person_id, access_token, getSolutions );
            marketPlaceData = await this.makePiersRequest( person_id, access_token, getMarketPlace );
            rewardsData = await this.makePiersRequest( person_id, access_token, getRewards );
        }
        
        if ( profileData != null ) {
            profileData.locale = profileData.locale.replace( /_/g, '-' );
        } else {
            profileData = {};
            profileData.locale = 'en-US';
        }

        let currentCompany;
        if ( companiesData != null ) {
            currentCompany = companiesData.companies.find( c => company_id === c.id );
        }

        const pierUser = {};

        pierUser.profile = profileData;          
        pierUser.companies = companiesData ? companiesData.companies : null;
        pierUser.solutions = solutionData ? solutionData.solutions : null;
        pierUser.marketplace = marketPlaceData;
        pierUser.cashier = rewardsData;
       
        this.setState( { 
            loaded: true,
            piersUser: pierUser,
            currentCompany: currentCompany }
        , () => {
             this.fetchStrings();
        } );

        if ( typeof this.props.onUserFetched === 'function' ) {
            this.props.onUserFetched( pierUser );
        }
    };

    fetchNavigation = async () => {
        const { access_token } = this.props.user;
        let data;
        let userData;
        if ( window.location.hostname.indexOf( 'biw.cloud' ) > 0 ) {
            data = await FetchGlobalStandaloneNavigation( access_token );
            userData = await FetchUserStandaloneNavigation( access_token );
        } else {
            data = await FetchGlobalNavigation( access_token );
            userData = await FetchUserNavigation( access_token );
        }
        this.setState( {
            navigationData: data,
            userNavData: userData
        } );
    };

    fetchNotifications = async () => {
        // const pollDelay = 45000;
        const { access_token } = this.props.user;
        const data = await FetchNotifications( access_token );
        const notifications = data && data.notifications
            ? data.notifications
            : [];

        // This next block is ugly, but it helps sidestep an issue with a list of
        // antd popovers nested inside of another popover. Basically add a dummy
        // item to the end of the list and then hide it from view on screen.
        if ( notifications.length > 0 ) {
            const last = notifications && notifications[ notifications.length - 1 ];
            const newLast = Object.assign( {}, last );
            newLast.id = 'dummyData';
            newLast.status = 'READ';
            notifications.push( newLast );
        }
    
        this.setState( {
            notifications
        } );

        // setTimeout( this.fetchNotifications, pollDelay );
    };
    fetchStrings = async () => {
        const { access_token, claims } = this.props.user;
        const { company_id } = claims;
        const { locale } = this.props;
        const profile = this.state.piersUser.profile || {};
        const profileLocale = profile.locale || undefined;

        await FetchStrings( access_token, locale || profileLocale, company_id );

        this.setState( {
            contentLoaded: true
        } );
    };

    render() {

        const {
            header,
            footer,
            leftNav,
            auth,
            user,
            locale,
            tokens,
            headerClassName,
            footerClassName,
            isUmbrella,
            guest,
            isDM,
            children,
            idUrl,
            restoring
        } = this.props;

        const {
            contentLoaded,
            currentCompany,
            loaded,
            navigationData,
            notifications,
            userNavData
        } = this.state;
        return (
            <Provider value={ { piersUser: this.state.piersUser, branding: this.state.branding } }>
                <Consumer>
                    { value => <div className="nkl-deck-app-root-outer">

                        {
                            //needed to pull everything out and put it in Main because the sidebar open/close was causing context to update which was rerendering the entire app-root and all of the children. Doing it this way prevents that
                            React.cloneElement( <Main />, { header, restoring, idUrl, footer, leftNav, auth, user, locale, tokens, headerClassName, footerClassName, isUmbrella, guest, isDM, contentLoaded, currentCompany, loaded, navigationData, userNavData, notifications, children, piersUser: value.piersUser, branding: value.branding } )
                        }
                    </div>
                    }
                </Consumer>
            </Provider>
        );
    }
}
